import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

export interface LoadingStage {
  /** Plain-language description of what the backend is doing at this point. */
  text: string;
  /** Elapsed seconds at which this message takes over (thresholds, ascending). */
  atSeconds: number;
}

/**
 * Load start timestamps keyed by `trackingKey`, shared across all instances and surviving
 * component destruction. Lets the elapsed timer/bar resume where it left off when the user
 * navigates away from a still-loading section and back, instead of restarting from 0.
 * Cleared when the load finishes (`active` → false).
 */
const TRACKER = new Map<string, number>();

/**
 * Scoped, per-instance loading indicator for slow round-trips (esearch → trendtech).
 *
 * Shows an asymptotic progress bar, time-calibrated "what's happening now" narration, a live
 * elapsed timer and an optional qualitative hint. There is NO hard countdown: load times for
 * these sections span ~50× (a few seconds for narrow themes to several minutes for broad ones)
 * and the family count isn't known client-side, so a fixed ETA would be misleading. The bar
 * eases toward ~95% over `estimateSeconds` and holds there until the parent flips `active` to
 * false — honest about uncertainty, never fakes 100%.
 *
 * Narration advances by `stages` thresholds calibrated to measured step durations: a narrow
 * theme that finishes in seconds only ever shows the first message; a broad theme walks through
 * all of them as the real backend steps run.
 *
 * Usage:
 *   <app-loading-progress
 *     [active]="showSpinner"
 *     title="Building performance history"
 *     [stages]="LOADING_STAGES"
 *     [estimateSeconds]="90"
 *     hint="Broad themes can take a few minutes.">
 *   </app-loading-progress>
 */
@Component({
  selector: 'app-loading-progress',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './loading-progress.component.html',
  styleUrl: './loading-progress.component.css',
})
export class LoadingProgressComponent implements OnChanges, OnDestroy {
  @Input() active = false;
  @Input() title = 'Loading';
  @Input() stages: LoadingStage[] = [{ text: 'Loading…', atSeconds: 0 }];
  /** Soft time-constant (seconds). Bar reaches ~63% at this mark, ~95% by ~3×. */
  @Input() estimateSeconds = 30;
  @Input() hint: string | null = null;
  /**
   * Optional stable id for this load (e.g. `tabId + ':perf_history'`). When set, the elapsed
   * timer persists across tab switches / re-mounts so it resumes instead of restarting.
   */
  @Input() trackingKey: string | null = null;

  progress = 0;       // 0–95 while active
  elapsedMs = 0;
  currentMessage = '';

  private startMs = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active']) {
      this.active ? this.start() : this.finish();
    }
  }

  ngOnDestroy(): void {
    // Stop only — keep the tracked start time so a re-mount mid-load resumes the timer.
    this.stop();
  }

  private start(): void {
    this.stop();
    const now = Date.now();
    // Resume from a persisted start if this load was already in flight before a re-mount.
    if (this.trackingKey) {
      if (!TRACKER.has(this.trackingKey)) TRACKER.set(this.trackingKey, now);
      this.startMs = TRACKER.get(this.trackingKey)!;
    } else {
      this.startMs = now;
    }
    this.currentMessage = this.stages[0]?.text ?? '';
    this.tick();   // render immediately so the resumed time shows without a 0:00 flash
    this.timer = setInterval(() => this.tick(), 200);
  }

  /** Load finished (active → false): stop ticking and drop the persisted start time. */
  private finish(): void {
    this.stop();
    if (this.trackingKey) TRACKER.delete(this.trackingKey);
  }

  private stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    this.elapsedMs = Date.now() - this.startMs;
    const elapsedS = this.elapsedMs / 1000;

    // Asymptotic ease — approaches 95% but never reaches it while loading.
    const tau = Math.max(this.estimateSeconds, 1);
    this.progress = 95 * (1 - Math.exp(-elapsedS / tau));

    // Pick the last stage whose threshold has passed.
    for (let i = this.stages.length - 1; i >= 0; i--) {
      if (elapsedS >= this.stages[i].atSeconds) {
        this.currentMessage = this.stages[i].text;
        break;
      }
    }
  }
}
